import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {FormService, LangService, LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {ApiService} from 'src/app/shared/services/api.service';
import {ScanService} from 'src/app/shared/services/scan.service';
import {EnrollService, enrollStepPaths} from 'src/app/shared/services/enroll.service';
import {UserPlatformRole} from 'src/app/shared/models/user';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {SyncService} from 'src/app/shared/services/sync.service';
import {differenceInDays} from 'date-fns';
import {parseError} from 'src/app/shared/utils/string';

@Component({
  selector: 'activation-step-voucher',
  templateUrl: './activation-step-voucher.component.html',
  styleUrls: ['./activation-step-voucher.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ActivationStepVoucherComponent implements OnInit{

  showScanQR: boolean;

  constructor(public nav: NavigateService,
    public api: ApiService,
    public enroll: EnrollService,
    public forms: FormService,
    private scan: ScanService,
    private loader: LoaderService,
    private lang: LangService,
    private formBuilder: FormBuilder,
    private sync: SyncService) {
  }

  form: FormGroup;
  submitted = false;
  daysRemaining: number;

  ngOnInit() {
    if(this.api.setNewVoucherMode) {
      if(this.api.isVoucherSoonExpired) {
        const now = new Date();
        this.daysRemaining = differenceInDays(this.api.userInfo.isForcedSub, now);
      }
      this.setForm();
    } else {
      const isSubActive = this.api.userRole.isCustomer && this.api.userInfo.isForcedSub && new Date(this.api.userInfo.isForcedSub).getTime() > Date.now();
      const nextStepInConfig = this.enroll.stepsConfig[this.enroll.currentConfigIndex].steps[this.enroll.currentStepIndex + 1];
      const prevStep = Object.keys(enrollStepPaths).find(key => enrollStepPaths[key] === this.nav.previousUrl.substring(1));
      const isGoingBack = nextStepInConfig === prevStep;
      if(isSubActive && (nextStepInConfig === prevStep)) {
        this.enroll.navigate('previous');
      }

      if(this.api.userInfo.role === UserPlatformRole.CUSTOMER) {
        if(isSubActive) this.enroll.navigate(isGoingBack ? 'previous' : 'next');
      } else {
        this.api.user(+this.scan.userScanData.data.sig).then(user => {
          if(user.isForcedSub && new Date(user.isForcedSub).getTime() > Date.now()) this.enroll.navigate(isGoingBack ? 'previous' : 'next');
        }).catch(() => {
          this.enroll.navigate('previous');
          setTimeout(() => {
            this.loader.loading(true, {type: 'error', message: this.lang.transform('qr.userNotRead')});
          }, 100);
        });
      }
    }
  }

  setForm() {
    this.form = this.formBuilder.group({
      VOUCHER: ['', [Validators.required]]});
  }

  progress() {
    let doPromise: Promise<any>;
    if(this.api.userInfo.role === UserPlatformRole.CUSTOMER) {
      doPromise = this.api.paySub(this.enroll.form.controls[this.enroll.fc.VOUCHER].value);
    } else {
      doPromise = this.api.payAdminSub(+this.scan.userScanData.data.sig, this.enroll.form.controls[this.enroll.fc.VOUCHER].value);
    }
    doPromise.then(() => {
      //TODO: Works only in case of 1year sub, should not be hardcoded
      if(this.api.userInfo.role === UserPlatformRole.CUSTOMER) this.api.userInfo.isForcedSub = new Date(Date.now() + 366 * 24 * 60 * 60 * 1000).toISOString();
      this.enroll.navigate('next');
    }).catch(err => {
      this.loader.loading(true, {type: 'error', message: this.lang.transform(this.voucherErrorKey(err, 'qr.voucherNotOk'))});
    });
  }

  private voucherErrorKey(err: any, fallback: string) {
    const key = parseError(err).baseError?.split(' ')[0];
    return key?.startsWith('client.extended.voucher') ? `${key}.DYN.server` : fallback;
  }

  submitForm() {
    if(!this.sync.isOnline) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('no.connection.signIn')});
    } else {
      this.submitted = true;
      if(this.forms.getFormErrors(this.form).length < 1) {
        this.submitted = false;
        this.loader.loading(true);
        return this.api.paySub(this.form.controls.VOUCHER.value).then(() => this.loader.loading(true, {type: 'valid', message: this.lang.transform('voucherUpdate.ok')}).then(() => this.api.info().then(() => {
          this.nav.to('user');
        }))).catch(err => {
          this.loader.loading(true, {type: 'error', message: this.lang.transform(this.voucherErrorKey(err, 'voucherUpdate.error'))});
        });
      }
    }
  }

  goBack() {
    if(this.api.setNewVoucherMode) {
      this.api.setNewVoucherMode = false;
      this.nav.to('more');
    } else {
      this.enroll.navigate('previous');
    }
  }

  signoutButton() {
    this.loader.loading(true, {type: 'warn', message: this.lang.transform('ask.signout'), btnLabel: this.lang.transform('confirm')}).then((done) =>{
      if(done) {
        this.api.signOut(true);
      }
    });
  }

  qrFound(result: any) {
    if(result) {
      const arr = this.jsonToArray(result);
      const base64 = btoa(String.fromCharCode.apply(null, arr));

      this.api.decryptQR(base64).then(decrypted => {
        this.showScanQR = false;
        if(decrypted) {
          this.enroll.form.controls[this.enroll.fc.VOUCHER].setValue(decrypted);
        } else {
          setTimeout(() => {
            this.loader.loading(true, {type: 'error', message: this.lang.transform('qr.voucherNotOk')});
          }, 500);
        }
      }).catch(() => {
        this.showScanQR = false;
        setTimeout(() => {
          this.loader.loading(true, {type: 'error', message: this.lang.transform('qr.voucherNotOk')});
        }, 500);
      });
    }
  }

  jsonToArray(json: string) {
    const str = JSON.parse(json);
    const ret = new Uint8Array(str.length);
    for(let i = 0; i < str.length; i++) {
      ret[i] = str.charCodeAt(i);
    }
    return ret;
  }
}
